import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { DatePickerInput } from "@/components/ui/date-picker-input";
import { Loader2, AlertCircle, CheckCircle2, X, Lock, ChevronsUpDown, Check } from "lucide-react";
import { format } from "date-fns";
import { useAppUsers } from "@/hooks/useAppUsers";
import { useSalesDevelopment } from "@/hooks/useSalesDevelopment";
import { useSalesUsers } from "@/hooks/useSalesUsers";
import { useUserRole } from "@/hooks/useUserRole";
import { detectCountryCode } from "@/lib/countryCodes";
import { CountryCodeSelect } from "@/components/common/CountryCodeSelect";
import { usePhoneDuplicateCheck } from "@/hooks/usePhoneDuplicateCheck";
import { removeCountryCode } from "@/lib/utils";
import { toast } from "sonner";
import { BUSINESS_UNITS } from "@/lib/businessUnits";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useTranslation } from "react-i18next";
import { getCitiesByState, fetchBrazilianCities, fetchAmericanCities } from "@/lib/locationUtils";
import { cn } from "@/lib/utils";

export default function ContactForm({ contact, onSubmit, onCancel, isProcessing, simplified = false }) {
  const { t } = useTranslation();
  const { users } = useAppUsers();
  const { salesDevelopment, isLoading: isLoadingSDR } = useSalesDevelopment();
  const { salesUsers, isLoading: isLoadingSalesUsers } = useSalesUsers();
  const { role: userRole } = useUserRole();
  
  const isVerificationLocked = contact?.verification_status === 'verified' && userRole !== 'admin';
  const [formData, setFormData] = useState({
    first_name: contact?.first_name || "",
    last_name: contact?.last_name || "",
    preferred_name: contact?.preferred_name || "",
    primary_email: contact?.primary_email || contact?.email || "",
    primary_phone: contact?.primary_phone 
      ? removeCountryCode(contact.primary_phone, contact.primary_phone_country_code || detectCountryCode(contact.primary_phone) || "+1")
      : "",
    primary_phone_country_code: contact?.primary_phone_country_code || detectCountryCode(contact?.primary_phone) || "+1",
    date_of_birth: contact?.date_of_birth || "",
    gender: contact?.gender || "",
    language: contact?.language || "",
    country_of_birth: contact?.country_of_birth || "",
    status: contact?.status || "active",
    // assigned_sales_consultant_id e sales_development_id removidos:
    // contato não tem mais dono — atribuição vive apenas no deal.
    contact_type: contact?.contact_type || "",
    customer_status: contact?.customer_status || "",
    churn_reason: contact?.churn_reason || "",
    satisfaction_level: contact?.satisfaction_level || "",
    verification_status: contact?.verification_status || "",
    do_not_call: contact?.do_not_call || false,
    do_not_message: contact?.do_not_message || false,
    contact_lifecycle_status: contact?.contact_lifecycle_status || "new",
    business_unit: contact?.business_unit || "",
    primary_address: (contact?.primary_address && typeof contact.primary_address === 'object')
      ? contact.primary_address
      : (contact?.address && typeof contact.address === 'object')
        ? contact.address
        : {
            type: "home",
            line1: "",
            line2: "",
            city: "",
            state: "",
            postal_code: "",
            country_code: "US"
          }
  });

  const phoneCheck = usePhoneDuplicateCheck(
    formData.primary_phone,
    formData.primary_phone_country_code,
    contact?.id
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // customer_status é obrigatório apenas para active_customer
    // past_customer pode ter customer_status NULL (decisão posterior)
    if (formData.contact_type === 'active_customer' && !formData.customer_status) {
      toast.error(t('contactForm.customerStatusRequired'));
      return;
    }
    
    if ((formData.customer_status === 'churned_client_decision' || formData.customer_status === 'churned_company_decision') && !formData.churn_reason?.trim()) {
      toast.error(t('contactForm.churnReasonRequired'));
      return;
    }
    
    const dataToSubmit = {
      first_name: formData.first_name,
      last_name: formData.last_name,
      preferred_name: formData.preferred_name || null,
      primary_email: formData.primary_email || null,
      primary_phone: formData.primary_phone || null,
      primary_phone_country_code: formData.primary_phone_country_code || "+1",
      date_of_birth: formData.date_of_birth || null,
      gender: formData.gender || null,
      language: formData.language || null,
      country_of_birth: formData.country_of_birth || null,
      status: formData.status || "active",
      // assigned_sales_consultant_id / sales_development_id removidos do payload
      contact_type: formData.contact_type || null,
      customer_status: formData.customer_status || null,
      churn_reason: formData.churn_reason || null,
      satisfaction_level: formData.satisfaction_level || null,
      verification_status: formData.verification_status || null,
      do_not_call: formData.do_not_call || false,
      do_not_message: formData.do_not_message || false,
      contact_lifecycle_status: formData.contact_lifecycle_status || "new",
      business_unit: formData.business_unit || null,
      primary_address: (
        formData.primary_address.line1?.trim() || 
        formData.primary_address.city?.trim() || 
        formData.primary_address.state?.trim() || 
        formData.primary_address.postal_code?.trim()
      ) ? formData.primary_address : null
    };

    console.log("ContactForm submitting data:", dataToSubmit);
    onSubmit(dataToSubmit);
  };

  const handleAddressChange = (field, value) => {
    setFormData(prev => {
      const newAddress = {
        ...prev.primary_address,
        [field]: value
      };
      
      // Clear city when country or state changes
      if (field === 'country_code' || field === 'state') {
        newAddress.city = '';
      }
      // Clear state and city when country changes
      if (field === 'country_code') {
        newAddress.state = '';
      }
      
      return {
        ...prev,
        primary_address: newAddress
      };
    });
  };

  const clearAddress = () => {
    setFormData(prev => ({
      ...prev,
      primary_address: {
        type: "home",
        line1: "",
        line2: "",
        city: "",
        state: "",
        postal_code: "",
        country_code: ""
      }
    }));
  };

  // Fetch cities from IBGE API for Brazilian states
  const { data: brazilianCities = [], isLoading: brCitiesLoading } = useQuery({
    queryKey: ['ibge-cities', formData.primary_address.state],
    queryFn: () => fetchBrazilianCities(formData.primary_address.state),
    enabled: formData.primary_address.country_code === 'BR' && !!formData.primary_address.state,
    staleTime: 1000 * 60 * 60 * 24, // Cache 24h
  });

  // Fetch cities from OpenDataSoft API for US states
  const { data: americanCities = [], isLoading: usCitiesLoading } = useQuery({
    queryKey: ['us-cities', formData.primary_address.state],
    queryFn: () => fetchAmericanCities(formData.primary_address.state),
    enabled: formData.primary_address.country_code === 'US' && !!formData.primary_address.state,
    staleTime: 1000 * 60 * 60 * 24, // Cache 24h
  });

  // Combined loading state
  const citiesLoading = brCitiesLoading || usCitiesLoading;

  // Get available cities based on selected state and country
  const availableCities = useMemo(() => {
    const countryCode = formData.primary_address.country_code;
    const stateCode = formData.primary_address.state;
    
    if (countryCode === 'BR') {
      return brazilianCities.length > 0 ? brazilianCities : getCitiesByState(stateCode, countryCode);
    }
    
    if (countryCode === 'US') {
      return americanCities.length > 0 ? americanCities : getCitiesByState(stateCode, countryCode);
    }
    
    return getCitiesByState(stateCode, countryCode);
  }, [formData.primary_address.state, formData.primary_address.country_code, brazilianCities, americanCities]);

  const [cityPopoverOpen, setCityPopoverOpen] = useState(false);
  const [citySearch, setCitySearch] = useState("");

  if (simplified) {
    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="first_name">{t('contactForm.firstName')} *</Label>
            <Input
              id="first_name"
              value={formData.first_name}
              onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              required
            />
          </div>

          <div>
            <Label htmlFor="last_name">{t('contactForm.lastName')} *</Label>
            <Input
              id="last_name"
              value={formData.last_name}
              onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
              required
            />
          </div>

          <div>
            <Label htmlFor="primary_email">{t('contactForm.email')}</Label>
            <Input
              id="primary_email"
              type="email"
              value={formData.primary_email}
              onChange={(e) => setFormData({ ...formData, primary_email: e.target.value })}
            />
          </div>

          <div>
            <Label htmlFor="primary_phone" className="flex items-center gap-2">
              {t('contactForm.phone')}
              {phoneCheck.checking && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
              {phoneCheck.isDuplicate && <AlertCircle className="h-4 w-4 text-destructive" />}
              {!phoneCheck.checking && !phoneCheck.isDuplicate && formData.primary_phone && formData.primary_phone.length >= 8 && (
                <CheckCircle2 className="h-4 w-4 text-green-500" />
              )}
            </Label>
            <div className="flex gap-2">
              <CountryCodeSelect
                value={formData.primary_phone_country_code}
                onChange={(value) => setFormData({ ...formData, primary_phone_country_code: value })}
              />
              <Input
                id="primary_phone"
                type="tel"
                value={formData.primary_phone}
                onChange={(e) => setFormData({ ...formData, primary_phone: e.target.value })}
                className={`flex-1 ${phoneCheck.isDuplicate ? 'border-destructive focus-visible:ring-destructive' : ''}`}
              />
            </div>
            {phoneCheck.isDuplicate && phoneCheck.existingContact && (
              <p className="text-sm text-destructive mt-1 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {t('contactForm.phoneDuplicate', { 
                  name: `${phoneCheck.existingContact.first_name} ${phoneCheck.existingContact.last_name}` 
                })}
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="country_of_birth">{t('contactForm.nationality')}</Label>
            <Select
              value={formData.country_of_birth}
              onValueChange={(value) => setFormData({ ...formData, country_of_birth: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('contactForm.selectCountry')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="United States">{t('countries.unitedStates')}</SelectItem>
                <SelectItem value="Brazil">{t('countries.brazil')}</SelectItem>
                <SelectItem value="Mexico">{t('countries.mexico')}</SelectItem>
                <SelectItem value="Canada">{t('countries.canada')}</SelectItem>
                <SelectItem value="United Kingdom">{t('countries.unitedKingdom')}</SelectItem>
                <SelectItem value="Germany">{t('countries.germany')}</SelectItem>
                <SelectItem value="France">{t('countries.france')}</SelectItem>
                <SelectItem value="Spain">{t('countries.spain')}</SelectItem>
                <SelectItem value="Italy">{t('countries.italy')}</SelectItem>
                <SelectItem value="Argentina">{t('countries.argentina')}</SelectItem>
                <SelectItem value="Colombia">{t('countries.colombia')}</SelectItem>
                <SelectItem value="Peru">{t('countries.peru')}</SelectItem>
                <SelectItem value="Chile">{t('countries.chile')}</SelectItem>
                <SelectItem value="Venezuela">{t('countries.venezuela')}</SelectItem>
                <SelectItem value="Ecuador">{t('countries.ecuador')}</SelectItem>
                <SelectItem value="Portugal">{t('countries.portugal')}</SelectItem>
                <SelectItem value="China">{t('countries.china')}</SelectItem>
                <SelectItem value="India">{t('countries.india')}</SelectItem>
                <SelectItem value="Japan">{t('countries.japan')}</SelectItem>
                <SelectItem value="South Korea">{t('countries.southKorea')}</SelectItem>
                <SelectItem value="Australia">{t('countries.australia')}</SelectItem>
                <SelectItem value="Other">{t('countries.other')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="outline" onClick={onCancel} disabled={isProcessing}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={isProcessing || phoneCheck.isDuplicate || phoneCheck.checking}>
            {isProcessing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t('common.saving')}
              </>
            ) : (
              contact ? t('contactForm.updateContact') : t('contactForm.createContact')
            )}
          </Button>
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Basic Information */}
      <div>
        <h3 className="text-lg font-semibold text-slate-900 mb-4">{t('contactForm.basicInformation')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="first_name">{t('contactForm.firstName')} *</Label>
            <Input
              id="first_name"
              value={formData.first_name}
              onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              required
            />
          </div>

          <div>
            <Label htmlFor="last_name">{t('contactForm.lastName')} *</Label>
            <Input
              id="last_name"
              value={formData.last_name}
              onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
              required
            />
          </div>

          <div>
            <Label htmlFor="preferred_name">{t('contactForm.preferredName')}</Label>
            <Input
              id="preferred_name"
              value={formData.preferred_name}
              onChange={(e) => setFormData({ ...formData, preferred_name: e.target.value })}
              placeholder={t('contactForm.preferredNamePlaceholder')}
            />
          </div>

          <div>
            <Label htmlFor="gender">{t('contactForm.gender')}</Label>
            <Select
              value={formData.gender}
              onValueChange={(value) => setFormData({ ...formData, gender: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('contactForm.selectGender')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="male">{t('gender.male')}</SelectItem>
                <SelectItem value="female">{t('gender.female')}</SelectItem>
                <SelectItem value="other">{t('gender.other')}</SelectItem>
                <SelectItem value="prefer_not_to_say">{t('gender.preferNotToSay')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="date_of_birth">{t('contactForm.dateOfBirth')}</Label>
            <DatePickerInput
              value={formData.date_of_birth || null}
              onChange={(date) => setFormData({ ...formData, date_of_birth: date ? format(date, 'yyyy-MM-dd') : '' })}
              maxDate={new Date()}
              minDate={new Date("1900-01-01")}
            />
          </div>

          <div>
            <Label htmlFor="language">{t('contactForm.preferredLanguage')}</Label>
            <Select
              value={formData.language}
              onValueChange={(value) => setFormData({ ...formData, language: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('contactForm.selectLanguage')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="english">{t('languages.english')}</SelectItem>
                <SelectItem value="portuguese">{t('languages.portuguese')}</SelectItem>
                <SelectItem value="spanish">{t('languages.spanish')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="country_of_birth">{t('contactForm.countryOfBirth')}</Label>
            <Select
              value={formData.country_of_birth}
              onValueChange={(value) => setFormData({ ...formData, country_of_birth: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('contactForm.selectCountry')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="United States">{t('countries.unitedStates')}</SelectItem>
                <SelectItem value="Brazil">{t('countries.brazil')}</SelectItem>
                <SelectItem value="Mexico">{t('countries.mexico')}</SelectItem>
                <SelectItem value="Canada">{t('countries.canada')}</SelectItem>
                <SelectItem value="United Kingdom">{t('countries.unitedKingdom')}</SelectItem>
                <SelectItem value="Germany">{t('countries.germany')}</SelectItem>
                <SelectItem value="France">{t('countries.france')}</SelectItem>
                <SelectItem value="Spain">{t('countries.spain')}</SelectItem>
                <SelectItem value="Italy">{t('countries.italy')}</SelectItem>
                <SelectItem value="Argentina">{t('countries.argentina')}</SelectItem>
                <SelectItem value="Colombia">{t('countries.colombia')}</SelectItem>
                <SelectItem value="Peru">{t('countries.peru')}</SelectItem>
                <SelectItem value="Chile">{t('countries.chile')}</SelectItem>
                <SelectItem value="Venezuela">{t('countries.venezuela')}</SelectItem>
                <SelectItem value="Ecuador">{t('countries.ecuador')}</SelectItem>
                <SelectItem value="Portugal">{t('countries.portugal')}</SelectItem>
                <SelectItem value="China">{t('countries.china')}</SelectItem>
                <SelectItem value="India">{t('countries.india')}</SelectItem>
                <SelectItem value="Japan">{t('countries.japan')}</SelectItem>
                <SelectItem value="South Korea">{t('countries.southKorea')}</SelectItem>
                <SelectItem value="Australia">{t('countries.australia')}</SelectItem>
                <SelectItem value="Other">{t('countries.other')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="status">{t('contactForm.status')}</Label>
            <Select
              value={formData.status}
              onValueChange={(value) => setFormData({ ...formData, status: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">{t('contactStatus.active')}</SelectItem>
                <SelectItem value="inactive">{t('contactStatus.inactive')}</SelectItem>
                <SelectItem value="prospecting">{t('contactStatus.prospecting')}</SelectItem>
                <SelectItem value="dormant">{t('contactStatus.dormant')}</SelectItem>
                <SelectItem value="churned">{t('contactStatus.churned')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Consultor e SDR foram removidos do contato — definidos apenas no deal */}
        </div>
      </div>

      {/* Status & Classification */}
      <div>
        <h3 className="text-lg font-semibold text-slate-900 mb-4">{t('contactForm.statusClassification')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="business_unit">{t('contactForm.businessUnit')}</Label>
            <Select
              value={formData.business_unit}
              onValueChange={(value) => setFormData({ ...formData, business_unit: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('contactForm.selectBU')} />
              </SelectTrigger>
              <SelectContent>
                {BUSINESS_UNITS.map(bu => (
                  <SelectItem key={bu.value} value={bu.value}>
                    {bu.flag} {bu.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="contact_type">{t('contactForm.contactType')}</Label>
            <Select
              value={formData.contact_type}
              onValueChange={(value) => {
                if (value === 'prospect') {
                  setFormData({ 
                    ...formData, 
                    contact_type: value,
                    customer_status: "",
                    churn_reason: ""
                  });
                } else {
                  setFormData({ ...formData, contact_type: value });
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('contactForm.selectType')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="prospect">{t('contactTypes.prospect')}</SelectItem>
                <SelectItem value="active_customer">{t('contactTypes.activeCustomer')}</SelectItem>
                <SelectItem value="past_customer">{t('contactTypes.pastCustomer')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {(formData.contact_type === 'active_customer' || formData.contact_type === 'past_customer') && (
            <>
              <div>
                <Label htmlFor="customer_status">{t('contactForm.customerStatus')} *</Label>
                <Select
                  value={formData.customer_status}
                  onValueChange={(value) => {
                    if (!value.startsWith('churned_')) {
                      setFormData({ ...formData, customer_status: value, churn_reason: "" });
                    } else {
                      setFormData({ ...formData, customer_status: value });
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('contactForm.selectStatus')} />
                  </SelectTrigger>
                  <SelectContent>
                    {formData.contact_type === 'active_customer' && (
                      <>
                        <SelectItem value="in_process">{t('customerStatus.inProcess')}</SelectItem>
                        <SelectItem value="churned_client_decision">{t('customerStatus.churnedClientDecision')}</SelectItem>
                        <SelectItem value="churned_company_decision">{t('customerStatus.churnedCompanyDecision')}</SelectItem>
                      </>
                    )}
                    {formData.contact_type === 'past_customer' && (
                      <>
                        <SelectItem value="completed">{t('customerStatus.completed')}</SelectItem>
                        <SelectItem value="churned_client_decision">{t('customerStatus.churnedClientDecision')}</SelectItem>
                        <SelectItem value="churned_company_decision">{t('customerStatus.churnedCompanyDecision')}</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>

              {(formData.customer_status === 'churned_client_decision' || formData.customer_status === 'churned_company_decision') && (
                <div className="md:col-span-2">
                  <Label htmlFor="churn_reason">{t('contactForm.churnReason')} *</Label>
                  <Textarea
                    id="churn_reason"
                    value={formData.churn_reason}
                    onChange={(e) => setFormData({ ...formData, churn_reason: e.target.value })}
                    placeholder={
                      formData.customer_status === 'churned_client_decision'
                        ? 'Explique por que o cliente desistiu...'
                        : formData.customer_status === 'churned_company_decision'
                        ? 'Explique por que a empresa encerrou o atendimento...'
                        : 'Descreva o motivo da desistência...'
                    }
                    className="min-h-[80px]"
                  />
                </div>
              )}
            </>
          )}

          <div>
            <Label htmlFor="satisfaction_level">{t('contactForm.satisfactionLevel')}</Label>
            <Select
              value={formData.satisfaction_level}
              onValueChange={(value) => setFormData({ ...formData, satisfaction_level: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('contactForm.selectLevel')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="promoter">{t('satisfactionLevel.promoterLabel')}</SelectItem>
                <SelectItem value="neutral">{t('satisfactionLevel.neutral')}</SelectItem>
                <SelectItem value="detractor">{t('satisfactionLevel.detractorLabel')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="verification_status" className="flex items-center gap-2">
              {t('contactForm.verificationStatus')}
              {isVerificationLocked && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Lock className="h-3.5 w-3.5 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{t('contactForm.verificationLocked')}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </Label>
            <Select
              value={formData.verification_status}
              onValueChange={(value) => setFormData({ ...formData, verification_status: value })}
              disabled={isVerificationLocked}
            >
              <SelectTrigger className={isVerificationLocked ? "opacity-60" : ""}>
                <SelectValue placeholder={t('contactForm.selectStatus')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unverified">{t('verificationStatus.unverified')}</SelectItem>
                <SelectItem value="verified">{t('verificationStatus.verified')}</SelectItem>
                <SelectItem value="invalid">{t('verificationStatus.invalid')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between space-x-2 p-4 border rounded-lg">
            <Label htmlFor="do_not_call" className="flex flex-col space-y-1 cursor-pointer">
              <span className="font-medium">{t('contactForm.doNotCall')}</span>
              <span className="text-sm text-muted-foreground">{t('contactForm.doNotCallDesc')}</span>
            </Label>
            <Switch
              id="do_not_call"
              checked={formData.do_not_call}
              onCheckedChange={(checked) => setFormData({ ...formData, do_not_call: checked })}
            />
          </div>

          <div className="flex items-center justify-between space-x-2 p-4 border rounded-lg">
            <Label htmlFor="do_not_message" className="flex flex-col space-y-1 cursor-pointer">
              <span className="font-medium">{t('contactForm.doNotMessage')}</span>
              <span className="text-sm text-muted-foreground">{t('contactForm.doNotMessageDesc')}</span>
            </Label>
            <Switch
              id="do_not_message"
              checked={formData.do_not_message}
              onCheckedChange={(checked) => setFormData({ ...formData, do_not_message: checked })}
            />
          </div>

          <div className="md:col-span-2">
            <Label htmlFor="contact_lifecycle_status">{t('contactForm.lifecycleStatus')}</Label>
            <Input
              id="contact_lifecycle_status"
              value={formData.contact_lifecycle_status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'New'}
              disabled
              className="bg-muted cursor-not-allowed"
            />
            <p className="text-xs text-muted-foreground mt-1">
              {t('contactForm.lifecycleStatusDesc')}
            </p>
          </div>
        </div>
      </div>

      {/* Contact Information */}
      <div>
        <h3 className="text-lg font-semibold text-slate-900 mb-4">{t('contactForm.contactInformation')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="primary_email">{t('contactForm.email')}</Label>
            <Input
              id="primary_email"
              type="email"
              value={formData.primary_email}
              onChange={(e) => setFormData({ ...formData, primary_email: e.target.value })}
            />
          </div>

          <div>
            <Label htmlFor="primary_phone" className="flex items-center gap-2">
              {t('contactForm.phoneNumber')}
              {phoneCheck.checking && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
              {phoneCheck.isDuplicate && <AlertCircle className="h-4 w-4 text-destructive" />}
              {!phoneCheck.checking && !phoneCheck.isDuplicate && formData.primary_phone && formData.primary_phone.length >= 8 && (
                <CheckCircle2 className="h-4 w-4 text-green-500" />
              )}
            </Label>
            <div className="flex gap-2">
              <CountryCodeSelect
                value={formData.primary_phone_country_code}
                onChange={(value) => setFormData({ ...formData, primary_phone_country_code: value })}
              />
              <Input
                id="primary_phone"
                type="tel"
                value={formData.primary_phone}
                onChange={(e) => setFormData({ ...formData, primary_phone: e.target.value })}
                className={`flex-1 ${phoneCheck.isDuplicate ? 'border-destructive focus-visible:ring-destructive' : ''}`}
                placeholder={t('contactForm.phoneNumberPlaceholder')}
              />
            </div>
            {phoneCheck.isDuplicate && phoneCheck.existingContact && (
              <p className="text-sm text-destructive mt-1 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {t('contactForm.phoneDuplicate', { 
                  name: `${phoneCheck.existingContact.first_name} ${phoneCheck.existingContact.last_name}` 
                })}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Address */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900">{t('contactForm.address')}</h3>
          <Button 
            type="button" 
            variant="ghost" 
            size="sm"
            onClick={clearAddress}
            className="text-muted-foreground hover:text-destructive"
          >
            <X className="w-4 h-4 mr-1" />
            {t('contactForm.clearAddress')}
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label htmlFor="line1">{t('contactForm.streetAddress')}</Label>
            <Input
              id="line1"
              value={formData.primary_address.line1}
              onChange={(e) => handleAddressChange('line1', e.target.value)}
              placeholder={t('contactForm.streetAddressPlaceholder')}
            />
          </div>

          <div className="md:col-span-2">
            <Label htmlFor="line2">{t('contactForm.apartment')}</Label>
            <Input
              id="line2"
              value={formData.primary_address.line2}
              onChange={(e) => handleAddressChange('line2', e.target.value)}
              placeholder={t('contactForm.apartmentPlaceholder')}
            />
          </div>

          <div>
            <Label htmlFor="city" className="flex items-center gap-2">
              {t('contactForm.city')}
              {citiesLoading && (
                <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
              )}
            </Label>
            {citiesLoading ? (
              <Button
                variant="outline"
                disabled
                className="w-full justify-between font-normal"
              >
                <span className="text-muted-foreground">{t('common.loading') || 'Carregando...'}</span>
                <Loader2 className="ml-2 h-4 w-4 animate-spin" />
              </Button>
            ) : availableCities.length > 0 ? (
              <Popover open={cityPopoverOpen} onOpenChange={setCityPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={cityPopoverOpen}
                    className="w-full justify-between font-normal"
                  >
                    {formData.primary_address.city || t('contactForm.selectCity')}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[300px] p-0" align="start">
                  <Command>
                    <CommandInput 
                      placeholder={t('contactForm.searchCity')} 
                      value={citySearch}
                      onValueChange={setCitySearch}
                    />
                    <CommandList
                      onWheel={(e) => e.stopPropagation()}
                      onTouchMove={(e) => e.stopPropagation()}
                    >
                      <CommandEmpty>
                        <div className="p-2 text-sm text-muted-foreground">
                          {t('contactForm.cityNotFound')}
                        </div>
                        {citySearch && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="w-full justify-start"
                            onClick={() => {
                              handleAddressChange('city', citySearch);
                              setCityPopoverOpen(false);
                              setCitySearch("");
                            }}
                          >
                            {t('contactForm.useCustomCity', { city: citySearch })}
                          </Button>
                        )}
                      </CommandEmpty>
                      <CommandGroup>
                        {availableCities
                          .filter(city => 
                            city.toLowerCase().includes(citySearch.toLowerCase())
                          )
                          .map((city) => (
                            <CommandItem
                              key={city}
                              value={city}
                              onSelect={() => {
                                handleAddressChange('city', city);
                                setCityPopoverOpen(false);
                                setCitySearch("");
                              }}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  formData.primary_address.city === city ? "opacity-100" : "opacity-0"
                                )}
                              />
                              {city}
                            </CommandItem>
                          ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            ) : (
              <Input
                id="city"
                value={formData.primary_address.city}
                onChange={(e) => handleAddressChange('city', e.target.value)}
                placeholder={formData.primary_address.state ? t('contactForm.typeCityName') : t('contactForm.selectStateFirst')}
                disabled={!formData.primary_address.country_code}
              />
            )}
          </div>

          <div>
            <Label htmlFor="state">{t('contactForm.state')}</Label>
            <Select
              value={formData.primary_address.state || ""}
              onValueChange={(value) => handleAddressChange('state', value === "__none__" ? "" : value)}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('contactForm.selectState')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— {t('common.none')} —</SelectItem>
                {formData.primary_address.country_code === 'US' && (
                  <>
                    <SelectItem value="AL">Alabama</SelectItem>
                    <SelectItem value="AK">Alaska</SelectItem>
                    <SelectItem value="AZ">Arizona</SelectItem>
                    <SelectItem value="AR">Arkansas</SelectItem>
                    <SelectItem value="CA">California</SelectItem>
                    <SelectItem value="CO">Colorado</SelectItem>
                    <SelectItem value="CT">Connecticut</SelectItem>
                    <SelectItem value="DE">Delaware</SelectItem>
                    <SelectItem value="FL">Florida</SelectItem>
                    <SelectItem value="GA">Georgia</SelectItem>
                    <SelectItem value="HI">Hawaii</SelectItem>
                    <SelectItem value="ID">Idaho</SelectItem>
                    <SelectItem value="IL">Illinois</SelectItem>
                    <SelectItem value="IN">Indiana</SelectItem>
                    <SelectItem value="IA">Iowa</SelectItem>
                    <SelectItem value="KS">Kansas</SelectItem>
                    <SelectItem value="KY">Kentucky</SelectItem>
                    <SelectItem value="LA">Louisiana</SelectItem>
                    <SelectItem value="ME">Maine</SelectItem>
                    <SelectItem value="MD">Maryland</SelectItem>
                    <SelectItem value="MA">Massachusetts</SelectItem>
                    <SelectItem value="MI">Michigan</SelectItem>
                    <SelectItem value="MN">Minnesota</SelectItem>
                    <SelectItem value="MS">Mississippi</SelectItem>
                    <SelectItem value="MO">Missouri</SelectItem>
                    <SelectItem value="MT">Montana</SelectItem>
                    <SelectItem value="NE">Nebraska</SelectItem>
                    <SelectItem value="NV">Nevada</SelectItem>
                    <SelectItem value="NH">New Hampshire</SelectItem>
                    <SelectItem value="NJ">New Jersey</SelectItem>
                    <SelectItem value="NM">New Mexico</SelectItem>
                    <SelectItem value="NY">New York</SelectItem>
                    <SelectItem value="NC">North Carolina</SelectItem>
                    <SelectItem value="ND">North Dakota</SelectItem>
                    <SelectItem value="OH">Ohio</SelectItem>
                    <SelectItem value="OK">Oklahoma</SelectItem>
                    <SelectItem value="OR">Oregon</SelectItem>
                    <SelectItem value="PA">Pennsylvania</SelectItem>
                    <SelectItem value="RI">Rhode Island</SelectItem>
                    <SelectItem value="SC">South Carolina</SelectItem>
                    <SelectItem value="SD">South Dakota</SelectItem>
                    <SelectItem value="TN">Tennessee</SelectItem>
                    <SelectItem value="TX">Texas</SelectItem>
                    <SelectItem value="UT">Utah</SelectItem>
                    <SelectItem value="VT">Vermont</SelectItem>
                    <SelectItem value="VA">Virginia</SelectItem>
                    <SelectItem value="WA">Washington</SelectItem>
                    <SelectItem value="WV">West Virginia</SelectItem>
                    <SelectItem value="WI">Wisconsin</SelectItem>
                    <SelectItem value="WY">Wyoming</SelectItem>
                  </>
                )}
                {formData.primary_address.country_code === 'BR' && (
                  <>
                    <SelectItem value="AC">Acre</SelectItem>
                    <SelectItem value="AL">Alagoas</SelectItem>
                    <SelectItem value="AP">Amapá</SelectItem>
                    <SelectItem value="AM">Amazonas</SelectItem>
                    <SelectItem value="BA">Bahia</SelectItem>
                    <SelectItem value="CE">Ceará</SelectItem>
                    <SelectItem value="DF">Distrito Federal</SelectItem>
                    <SelectItem value="ES">Espírito Santo</SelectItem>
                    <SelectItem value="GO">Goiás</SelectItem>
                    <SelectItem value="MA">Maranhão</SelectItem>
                    <SelectItem value="MT">Mato Grosso</SelectItem>
                    <SelectItem value="MS">Mato Grosso do Sul</SelectItem>
                    <SelectItem value="MG">Minas Gerais</SelectItem>
                    <SelectItem value="PA">Pará</SelectItem>
                    <SelectItem value="PB">Paraíba</SelectItem>
                    <SelectItem value="PR">Paraná</SelectItem>
                    <SelectItem value="PE">Pernambuco</SelectItem>
                    <SelectItem value="PI">Piauí</SelectItem>
                    <SelectItem value="RJ">Rio de Janeiro</SelectItem>
                    <SelectItem value="RN">Rio Grande do Norte</SelectItem>
                    <SelectItem value="RS">Rio Grande do Sul</SelectItem>
                    <SelectItem value="RO">Rondônia</SelectItem>
                    <SelectItem value="RR">Roraima</SelectItem>
                    <SelectItem value="SC">Santa Catarina</SelectItem>
                    <SelectItem value="SP">São Paulo</SelectItem>
                    <SelectItem value="SE">Sergipe</SelectItem>
                    <SelectItem value="TO">Tocantins</SelectItem>
                  </>
                )}
                {formData.primary_address.country_code !== 'US' && formData.primary_address.country_code !== 'BR' && (
                  <SelectItem value="Other">{t('countries.other')}</SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="postal_code">{t('contactForm.postalCode')}</Label>
            <Input
              id="postal_code"
              value={formData.primary_address.postal_code}
              onChange={(e) => handleAddressChange('postal_code', e.target.value)}
            />
          </div>

          <div>
            <Label htmlFor="country_code">{t('contactForm.country')}</Label>
            <Select
              value={formData.primary_address.country_code || ""}
              onValueChange={(value) => handleAddressChange('country_code', value === "__none__" ? "" : value)}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('contactForm.selectCountry')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— {t('common.none')} —</SelectItem>
                <SelectItem value="BR">{t('countries.brazil')}</SelectItem>
                <SelectItem value="US">{t('countries.unitedStates')}</SelectItem>
                <SelectItem value="MX">{t('countries.mexico')}</SelectItem>
                <SelectItem value="CA">{t('countries.canada')}</SelectItem>
                <SelectItem value="GB">{t('countries.unitedKingdom')}</SelectItem>
                <SelectItem value="ES">{t('countries.spain')}</SelectItem>
                <SelectItem value="FR">{t('countries.france')}</SelectItem>
                <SelectItem value="DE">{t('countries.germany')}</SelectItem>
                <SelectItem value="IT">{t('countries.italy')}</SelectItem>
                <SelectItem value="AR">{t('countries.argentina')}</SelectItem>
                <SelectItem value="CO">{t('countries.colombia')}</SelectItem>
                <SelectItem value="PE">{t('countries.peru')}</SelectItem>
                <SelectItem value="CL">{t('countries.chile')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isProcessing}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={isProcessing || phoneCheck.isDuplicate || phoneCheck.checking}>
          {isProcessing ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {t('common.saving')}
            </>
          ) : (
            contact ? t('contactForm.updateContact') : t('contactForm.createContact')
          )}
        </Button>
      </div>
    </form>
  );
}
